import type React from 'react';
import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

export interface EditField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'datetime' | 'decimal' | 'text';
  readonly?: boolean;
}

interface EditRowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: EditField[];
  row: Record<string, unknown> | null;
  onSave: (data: Record<string, unknown>) => void;
  isSaving?: boolean;
  title?: string;
}

export function EditRowDialog({
  open,
  onOpenChange,
  fields,
  row,
  onSave,
  isSaving,
  title = 'Edit Row'
}: EditRowDialogProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (row && open) {
      const initial: Record<string, unknown> = {};
      for (const field of fields) {
        initial[field.name] = row[field.name] ?? '';
      }
      setFormData(initial);
    }
  }, [row, open, fields]);

  const handleChange = (name: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const changes: Record<string, unknown> = {};
    for (const field of fields) {
      if (field.readonly) {
        continue;
      }
      const original = row?.[field.name];
      const current = formData[field.name];
      if (String(original ?? '') !== String(current ?? '')) {
        if (field.type === 'number') {
          changes[field.name] = Number(current);
        } else if (field.type === 'decimal') {
          changes[field.name] = Number(current);
        } else if (field.type === 'boolean') {
          changes[field.name] = Boolean(current);
        } else {
          changes[field.name] = current;
        }
      }
    }
    if (Object.keys(changes).length > 0) {
      onSave(changes);
    } else {
      onOpenChange(false);
    }
  };

  const renderField = (field: EditField) => {
    const value = formData[field.name];

    if (field.readonly) {
      return (
        <div key={field.name} className="grid gap-2">
          <Label className="text-muted-foreground">{field.label}</Label>
          <Input value={String(value ?? '')} disabled className="bg-muted/50 text-muted-foreground" />
        </div>
      );
    }

    if (field.type === 'boolean') {
      return (
        <div key={field.name} className="flex items-center justify-between rounded-md border px-3 py-2">
          <Label>{field.label}</Label>
          <Switch checked={Boolean(value)} onCheckedChange={checked => handleChange(field.name, checked)} />
        </div>
      );
    }

    if (field.type === 'text') {
      return (
        <div key={field.name} className="grid gap-2">
          <Label>{field.label}</Label>
          <Textarea value={String(value ?? '')} onChange={e => handleChange(field.name, e.target.value)} rows={3} />
        </div>
      );
    }

    if (field.type === 'number' || field.type === 'decimal') {
      return (
        <div key={field.name} className="grid gap-2">
          <Label>{field.label}</Label>
          <Input
            type="number"
            step={field.type === 'decimal' ? 'any' : '1'}
            value={String(value ?? '')}
            onChange={e => handleChange(field.name, e.target.value)}
          />
        </div>
      );
    }

    if (field.type === 'datetime') {
      const strVal = value ? String(value) : '';
      return (
        <div key={field.name} className="grid gap-2">
          <Label>{field.label}</Label>
          <Input
            type="datetime-local"
            value={strVal ? new Date(strVal).toISOString().slice(0, 16) : ''}
            onChange={e => handleChange(field.name, e.target.value ? new Date(e.target.value).toISOString() : '')}
          />
        </div>
      );
    }

    return (
      <div key={field.name} className="grid gap-2">
        <Label>{field.label}</Label>
        <Input value={String(value ?? '')} onChange={e => handleChange(field.name, e.target.value)} />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Make changes to the row fields below. Click save when done.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">{fields.map(renderField)}</div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
